import { useTranslation } from 'react-i18next';

import type { TaskTabType } from '@/types';

type EmptyReason = 'noTasks' | 'search' | 'filter';

interface EmptyTaskStateProps {
  tab: TaskTabType;
  hasInProgressTask?: boolean | undefined;
  emptyReason?: EmptyReason | undefined;
  onNavigateToTab?: ((tab: TaskTabType) => void) | undefined;
}

/**
 * Empty state view matching Xamarin EmptyListView + ApplyEmptyMessage logic.
 *
 * Per-tab configuration (DashboardPageViewModel.cs):
 *   My Tasks (no in-progress): my_task_empty.svg  → "Please go to Upcoming tasks and start accepting." → button "Lets Go!" → navigate to Upcoming
 *   My Tasks (in-progress):    in_progress_empty.svg → "Task is in progress" / "You have an ongoing task in your list"
 *   Upcoming:                  upcoming_empty.svg → "No new tasks!" / "There are no active task..." → button "Go to my task" → navigate to My Tasks
 *   Completed:                 completed_empty.svg → "No tasks completed!" / "You haven't completed any tasks yet..." → button "Lets Go!" → navigate to My Tasks
 *
 * Navigation (NavigateToTabCommandHandler):
 *   My Tasks  → Upcoming
 *   Upcoming  → My Tasks
 *   Completed → My Tasks
 */
function getEmptyConfig(tab: TaskTabType, hasInProgressTask: boolean) {
  if (tab === 'myTasks' && hasInProgressTask) {
    return {
      image: '/icons/in_progress_empty.svg',
      header: 'dashboard.emptyInProgress',
      body: 'dashboard.emptyInProgressBody',
      buttonText: null,
      navigateTo: null,
    };
  }

  const configs: Record<
    TaskTabType,
    {
      image: string;
      header: string;
      body: string;
      buttonText: string | null;
      navigateTo: TaskTabType | null;
    }
  > = {
    myTasks: {
      image: '/icons/my_task_empty.svg',
      header: 'dashboard.emptyMyTasks',
      body: '',
      buttonText: 'dashboard.emptyMyTasksButton',
      navigateTo: 'upcoming',
    },
    upcoming: {
      image: '/icons/upcoming_empty.svg',
      header: 'dashboard.emptyUpcoming',
      body: 'dashboard.emptyUpcomingBody',
      buttonText: 'dashboard.emptyUpcomingButton',
      navigateTo: 'myTasks',
    },
    completed: {
      image: '/icons/completed_empty.svg',
      header: 'dashboard.emptyCompleted',
      body: 'dashboard.emptyCompletedBody',
      buttonText: 'dashboard.emptyCompletedButton',
      navigateTo: 'myTasks',
    },
  };

  return configs[tab];
}

export function EmptyTaskState({
  tab,
  hasInProgressTask,
  emptyReason,
  onNavigateToTab,
}: EmptyTaskStateProps) {
  const { t } = useTranslation();
  const reason = emptyReason ?? 'noTasks';

  // Xamarin SearchCommandHandler: search yields no results → SearchEmptyMessage, no body, no button
  if (reason === 'search') {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-6 text-center">
        <img
          src="/icons/upcoming_empty.svg"
          alt={t('dashboard.searchEmptyMessage')}
          className="mb-2 max-h-[40vh] max-w-[80vw]"
        />
        <h3 className="mb-2 text-xl font-bold text-[#243743]">
          {t('dashboard.searchEmptyMessage')}
        </h3>
      </div>
    );
  }

  // Xamarin ApplyEmptyMessage: filter yields no results but tasks exist → EmptyFilterBody
  if (reason === 'filter') {
    const config = getEmptyConfig(tab, hasInProgressTask ?? false);
    return (
      <div className="flex flex-col items-center justify-center px-4 py-6 text-center">
        <img src={config.image} alt={t(config.header)} className="mb-2 max-h-[40vh] max-w-[80vw]" />
        <h3 className="mb-2 text-xl font-bold text-[#243743]">{t(config.header)}</h3>
        <p className="mb-4 max-w-xs text-sm text-[#243743]">{t('dashboard.emptyFilterBody')}</p>
      </div>
    );
  }

  const config = getEmptyConfig(tab, hasInProgressTask ?? false);

  return (
    <div className="flex flex-col items-center justify-center px-4 py-6 text-center">
      <img src={config.image} alt={t(config.header)} className="mb-2 max-h-[40vh] max-w-[80vw]" />

      {/* Header */}
      <h3 className="mb-2 text-xl font-bold text-[#243743]">{t(config.header)}</h3>

      {/* Body */}
      {config.body && <p className="mb-4 max-w-xs text-sm text-[#243743]">{t(config.body)}</p>}

      {/* Navigation button */}
      {config.buttonText && config.navigateTo && onNavigateToTab && (
        <button
          onClick={() => {
            if (config.navigateTo) onNavigateToTab(config.navigateTo);
          }}
          className="mt-2 rounded-lg bg-[#1357A0] px-6 py-3 text-sm font-bold text-white"
        >
          {t(config.buttonText)}
        </button>
      )}
    </div>
  );
}
